// eslint-disable-next-line no-use-before-define
import React, { useEffect } from 'react';
import { styled } from '@mui/system';
import { Field, FieldArray, useFormikContext } from 'formik';

import { HelperMessage, UserIcon } from 'shared-components/components';
import { GraphQLResponse } from 'shared-components/interfaces';
import TextAreaField from './TextAreaField';
import { Button, IconButton } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

interface Props {
  name: string;
  fieldText?: string;
  placeholder: string;
  addButtonText?: string;
  helperMessageType?: string;
  createMutation: (value: string, field: string) => any;
  deleteMutation: (id: string) => any;
  updateMutation: ({ id, name, value }: any) => any;
  values: any;
  parentField: string;
  textFieldValue: string;
  textFieldName: string;
  userIconFieldName?: string;
  userTypeValue?: string;
  isManagementForm?: boolean;
}

interface StyledProps {
  isLastItem: boolean;
}

const MultipleTextArea = styled('div')<StyledProps>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ isLastItem }: StyledProps) => (!isLastItem ? '12px' : '0')};
`;

const TextAreaFieldGroup = ({
  name,
  createMutation,
  deleteMutation,
  updateMutation,
  fieldText,
  placeholder,
  addButtonText,
  helperMessageType,
  parentField,
  values,
  textFieldValue,
  textFieldName,
  userIconFieldName,
  userTypeValue,
  isManagementForm = false,
}: Props): JSX.Element => {
  const defaultTextfieldValue = '';
  const { setFieldTouched, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values?.length === 0) {
      createMutation(name, defaultTextfieldValue).then((result: GraphQLResponse) => {
        const newItem: any = Object.values(result.data)[0];
        if (userIconFieldName) {
          newItem[parentField][userIconFieldName] = '';
        }
        setFieldValue(name, [{ ...newItem[parentField] }], false);
      });
    }
  }, [values]);

  const showAdd =
    isManagementForm || (values?.length > 0 && values.filter((item: any) => item[textFieldValue] !== '').length > 0);

  return (
    <FieldArray
      name={name}
      render={({ push, remove }) => {
        return (
          <>
            {values?.length > 0 &&
              values.map((item: any, idx: number) => {
                const isLastItem = idx === values.length - 1;
                const styledProps = { isLastItem };
                const { id } = item;
                const textAreaName = `${name}[${idx}].${textFieldValue}`;

                // Fields are only used when a user icon needs to be rendered
                let userType;
                let userTypeName = '';
                if (userIconFieldName) {
                  userType = item[userIconFieldName];
                  userTypeName = `${name}[${idx}].${userIconFieldName}`;
                }

                return (
                  <MultipleTextArea {...styledProps} key={idx}>
                    <Field
                      name={textAreaName}
                      component={TextAreaField}
                      placeholder={placeholder}
                      updateMutation={(value: string) => {
                        updateMutation({ variables: { id, [textFieldName]: name, [textFieldValue]: value } });
                        setFieldTouched(name, true);
                        if (userIconFieldName) {
                          setFieldValue(userTypeName, userTypeValue);
                        }
                      }}
                      multiline
                      maxLength={2048}
                    />
                    {userIconFieldName && <UserIcon userType={userType} />}
                    {values.length > 1 && (
                      <IconButton
                        data-testid={`remove-icon-${name}-${idx}`}
                        sx={{ marginLeft: 0.5 }}
                        onClick={() => {
                          remove(idx);
                          deleteMutation(id);
                        }}
                        color="primary">
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </MultipleTextArea>
                );
              })}
            {showAdd && (
              <>
                {fieldText && <HelperMessage fieldName={name} fieldText={fieldText} helperType={helperMessageType} />}
                <Button
                  variant="text"
                  sx={{ marginRight: 'auto', marginTop: 1 }}
                  startIcon={<AddCircleOutline color="primary" />}
                  onClick={() => {
                    if (isManagementForm) {
                      return createMutation(name, defaultTextfieldValue);
                    } else {
                      return createMutation(name, defaultTextfieldValue).then((result: GraphQLResponse) => {
                        const newItem: any = Object.values(result.data)[0];
                        push({ ...newItem[parentField] });
                      });
                    }
                  }}>
                  {addButtonText}
                </Button>
              </>
            )}
          </>
        );
      }}
    />
  );
};

export default TextAreaFieldGroup;
